// How many employees a plan has, counting enrollments that have not started
// yet (DEV-317 / BUG-005): Available Plans said "2 employees" for Gold 80
// while three employees held it, because the pending (Nov 1) election was
// dropped; once every election is pending the column read "0 employees" for a
// plan with three elections. Available Plans and the Benefits Overview
// "Enrolled" column both read this one helper so they tell the same story.
import { format } from "date-fns";
import { parseDateLocal } from "@/lib/utils";

/**
 * NEEDS PRODUCT DECISION (DEV-317). How the Enrolled column shows pending
 * (future-dated) enrollments:
 *   "split"    (Option B) "2 active · 1 starting Nov 1"  — loses nothing
 *   "combined" (Option A) "3 employees"                  — active + pending
 * Flip this one constant to switch every surface at once.
 */
export const ENROLLED_LABEL_MODE: "split" | "combined" = "split";

export interface PlanEnrollmentLike {
  employeeId?: number | null;
  dependentId?: number | null;
  status?: string | null;
  waivedCoverage?: boolean | null;
  effectiveDate?: string | Date | null;
  endDate?: string | Date | null;
}

export interface PlanEnrollmentCounts {
  /** Distinct employees whose coverage is in force today. */
  active: number;
  /** Distinct employees whose coverage starts after today (and who are not already active). */
  pending: number;
  /** Earliest start among the pending employees, as a local calendar date. */
  nextStart: Date | null;
}

const EXCLUDED_STATUSES = new Set(["waived", "terminated", "deleted", "cancelled", "canceled"]);

const startOfDay = (d: Date): Date => new Date(d.getFullYear(), d.getMonth(), d.getDate());

/**
 * Counts distinct employees on one plan's enrollment rows. Dependent rows are
 * skipped (the employee row carries the election), and waived, terminated,
 * cancelled/deleted and ended rows are excluded. Dates are read as local
 * calendar dates so a "2026-11-01" start is Nov 1 in every US time zone.
 */
export function planEnrollmentCounts(
  enrollments: PlanEnrollmentLike[] | null | undefined,
  today: Date = new Date(),
): PlanEnrollmentCounts {
  const todayMs = startOfDay(today).getTime();
  const activeIds = new Set<number>();
  const pendingStartById = new Map<number, number | null>();

  for (const e of enrollments || []) {
    if (!e || e.dependentId != null) continue;
    const employeeId = Number(e.employeeId);
    if (!Number.isFinite(employeeId)) continue;
    const status = String(e.status ?? "active").toLowerCase();
    if (EXCLUDED_STATUSES.has(status) || e.waivedCoverage === true) continue;
    if (status !== "active" && status !== "pending") continue;

    const end = e.endDate ? parseDateLocal(e.endDate) : null;
    if (end && end.getTime() < todayMs) continue; // ended

    const start = e.effectiveDate ? parseDateLocal(e.effectiveDate) : null;
    const notYetEffective = start ? start.getTime() > todayMs : status === "pending";
    if (!notYetEffective) {
      activeIds.add(employeeId);
      continue;
    }
    const startMs = start ? start.getTime() : null;
    const prev = pendingStartById.get(employeeId);
    if (prev === undefined || (startMs != null && (prev == null || startMs < prev))) {
      pendingStartById.set(employeeId, startMs);
    }
  }

  let pending = 0;
  let nextStartMs: number | null = null;
  pendingStartById.forEach((startMs, employeeId) => {
    if (activeIds.has(employeeId)) return; // already covered; the pending row is a later plan year
    pending += 1;
    if (startMs != null && (nextStartMs == null || startMs < nextStartMs)) nextStartMs = startMs;
  });

  return {
    active: activeIds.size,
    pending,
    nextStart: nextStartMs == null ? null : new Date(nextStartMs),
  };
}

const employeesNoun = (n: number) => (n === 1 ? "employee" : "employees");

/**
 * "N employees" / "N starting Nov 1" / "N active · M starting Nov 1" /
 * "0 employees" (Option B), or "N employees" over active + pending (Option A).
 */
export function formatEnrolledLabel(
  counts: PlanEnrollmentCounts,
  mode: "split" | "combined" = ENROLLED_LABEL_MODE,
): string {
  const { active, pending, nextStart } = counts;
  if (mode === "combined" || pending === 0) {
    const total = mode === "combined" ? active + pending : active;
    return `${total} ${employeesNoun(total)}`;
  }
  const starting = nextStart ? `${pending} starting ${format(nextStart, "MMM d")}` : `${pending} pending`;
  return active === 0 ? starting : `${active} active · ${starting}`;
}
